import { useEffect, useMemo, useRef, useState } from 'react';
import { Maybe, QuestionItem, AnswersItem } from '__generated__/graphql';
import SurveyProgressBar from 'components/SurveyProgressBar';
import QuestionCard from './QuestionCard';
import { getProgress, setProgress, Section } from '../persistence/localProgress';
import { useUser } from '../context/userContext';
import { useBackgroundAnswerSave } from '../hooks/useBackgroundAnswerSave';
import Loading from './Loading';

type TotalsBySection = Record<Section, number>;
type AnsweredBySection = Partial<Record<Section, number>>;

type QuestionPagerProps = {
  questions: Maybe<QuestionItem>[];
  section: Section;
  onSectionComplete: (answersSubset: AnswersItem) => void;
  onProgressUpdate?: () => void;
  totals?: TotalsBySection;
  answered?: AnsweredBySection;
};

export default function QuestionPager({ questions, section, onSectionComplete, onProgressUpdate, totals, answered }: QuestionPagerProps) {
  const { company } = useUser();
  const companyId = company?.id;
  const { saveAnswer } = useBackgroundAnswerSave();

  const sanitizedQuestions = useMemo(() => {
    const filtered = (questions || []).filter(Boolean) as QuestionItem[];
    // Ensure deterministic ordering across reloads to keep progress consistent
    return filtered.sort((a, b) => String(a.id).localeCompare(String(b.id)));
  }, [questions]);
  const total = sanitizedQuestions.length;

  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [answersMap, setAnswersMap] = useState<Record<string, boolean | null>>({});
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  const initializedRef = useRef(false);

  const ready = Boolean(companyId) && total > 0;

  // Initialize from local progress once when companyId and questions are ready
  useEffect(() => {
    if (!ready) return;
    if (initializedRef.current) return;
    const initial = getProgress(companyId, section);
    const initialAnswers = initial.answers || {};
    setAnswersMap(initialAnswers);

    // Resume at the first unanswered question (fall back to last question when all answered)
    const firstUnanswered = sanitizedQuestions.findIndex((q) => !q.id || initialAnswers[q.id] == null);
    const resumeIndex = firstUnanswered === -1 ? Math.max(total - 1, 0) : firstUnanswered;
    setCurrentIndex(resumeIndex);
    initializedRef.current = true;
  }, [ready, companyId, section, total, sanitizedQuestions]);

  // Persist progress only when companyId is known
  useEffect(() => {
    if (!ready) return;
    setProgress(companyId, section, { currentIndex, answers: answersMap });
    if (onProgressUpdate) onProgressUpdate();
  }, [companyId, section, currentIndex, answersMap, ready, onProgressUpdate]);

  const currentQuestion = sanitizedQuestions[currentIndex];
  const nextQuestion = currentIndex < total - 1 ? sanitizedQuestions[currentIndex + 1] : undefined;
  const prevQuestion = currentIndex > 0 ? sanitizedQuestions[currentIndex - 1] : undefined;

  // Preload next and previous question images for smooth navigation
  useEffect(() => {
    if (nextQuestion?.pic) {
      const img = new Image();
      img.src = `${process.env.REACT_APP_CLOUDFRONT_URL}${nextQuestion.pic}`;
    }
    if (prevQuestion?.pic) {
      const img = new Image();
      img.src = `${process.env.REACT_APP_CLOUDFRONT_URL}${prevQuestion.pic}`;
    }
  }, [nextQuestion?.pic, prevQuestion?.pic]);

  const handleAnswer = (value: boolean) => {
    if (!currentQuestion?.id || isTransitioning) return;

    const nextAnswers = { ...answersMap, [currentQuestion.id]: value };
    setAnswersMap(nextAnswers);

    // fire-and-forget background save for this single answer
    saveAnswer(currentQuestion.id, value);

    const isLast = currentIndex >= total - 1;
    if (isLast) {
      // prepare subset for this section only
      const subset = Object.keys(nextAnswers).reduce((acc, k) => {
        acc[k as keyof AnswersItem] = nextAnswers[k] as boolean | null;
        return acc;
      }, {} as Partial<AnswersItem>) as AnswersItem;
      // Persist final progress synchronously before navigation so the top bar shows completion
      if (ready) {
        setProgress(companyId, section, { currentIndex: total - 1, answers: nextAnswers });
        if (onProgressUpdate) onProgressUpdate();
      }
      onSectionComplete(subset);
      return;
    }

    // Start transition: fade out, then navigate
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((i) => Math.min(i + 1, total - 1));
      setIsTransitioning(false);
    }, 300); // Match the fade-out duration
  };

  const handleBack = () => {
    if (isTransitioning || currentIndex === 0) return;
    // Start transition: fade out, then navigate
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((i) => Math.max(i - 1, 0));
      setIsTransitioning(false);
    }, 300); // Match the fade-out duration
  };

  // Only allow forward navigation via arrows to questions that are already answered
  const canForward = Boolean(currentIndex < total - 1 && nextQuestion?.id && answersMap[nextQuestion.id] != null);
  const handleForwardNav = () => {
    if (!canForward || isTransitioning) return;
    // Start transition: fade out, then navigate
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((i) => Math.min(i + 1, total - 1));
      setIsTransitioning(false);
    }, 300); // Match the fade-out duration
  };

  if (!ready) {
    return (
      <div className="flex flex-col items-center justify-center gap-6 text-center text-white h-full">
        <Loading isComponent={true} />
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-center">
      <div className="text-white text-sm opacity-80 mb-1">{`${Math.min(currentIndex + 1, total)} of ${total}`}</div>
      <QuestionCard question={currentQuestion} onAnswer={handleAnswer} isTransitioning={isTransitioning} />
      {totals && answered ? (
        <div className="w-full flex items-center justify-center mt-3 sm:mt-4">
          <SurveyProgressBar
            totals={totals}
            answered={answered}
            onBack={currentIndex > 0 ? handleBack : undefined}
            onForward={canForward ? handleForwardNav : undefined}
            canGoBack={currentIndex > 0}
            canGoForward={canForward}
          />
        </div>
      ) : null}
    </div>
  );
}
